
import { api } from './base';
import type { FormSchema } from './form-schema';

export * from './form-schema';

export interface FormVersionInfo {
  id: string;
  subcategoryId: string | null;
  templateId: string | null;
  version: number;
  schema: FormSchema;
  publishedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface FormBindingInfo {
  templateId: string;
  templateName: string;
}

export interface FormVersionHistory {
  own: FormVersionInfo[];
  template: FormVersionInfo[];
}

export interface FormTemplate {
  id: string;
  name: string;
  description: string | null;
  group: string | null;
  schema: FormSchema;
  createdAt?: string;
  updatedAt?: string;
}

export type ApplyMode = 'replace' | 'append';

export interface BindResult {
  subcategoryId: string;
  ok: boolean;
  error?: string;
}

export interface TemplateBinding {
  subcategoryId: string;
  subcategoryName: string;
  categoryId: string;
  categoryName: string;
}

export interface PreflightRow extends TemplateBinding {
  boundToOtherTemplateId: string | null;
  boundToOtherTemplateName: string | null;
  hasOwnPublishedForm: boolean;
}

export interface CustomFormRow extends TemplateBinding {
  fieldCount: number;
  publishedAt: string | null;
}

export interface CatalogOption {
  id: string;
  name: string;
  isDefault: boolean;
}

export interface BindingTreeSubcategory {
  id: string;
  name: string;
  formTemplateId: string | null;
}

export interface BindingTreeCategory {
  id: string;
  name: string;
  subcategories: BindingTreeSubcategory[];
}

export const formsApi = {
  getPublished: (subcategoryId: string) =>
    api.get<FormVersionInfo | null>(`/api/v1/subcategories/${subcategoryId}/form`),
  getDraft: (subcategoryId: string) =>
    api.get<FormVersionInfo | null>(`/api/v1/admin/subcategories/${subcategoryId}/form/draft`),
  getBinding: (subcategoryId: string) =>
    api.get<FormBindingInfo | null>(`/api/v1/admin/subcategories/${subcategoryId}/form/binding`),
  getVersions: (subcategoryId: string) =>
    api.get<FormVersionHistory>(`/api/v1/admin/subcategories/${subcategoryId}/form/versions`),
  saveDraft: (subcategoryId: string, schema: FormSchema) =>
    api.put<FormVersionInfo>(`/api/v1/admin/subcategories/${subcategoryId}/form/draft`, { schema }),
  publish: (subcategoryId: string) =>
    api.post<FormVersionInfo>(`/api/v1/admin/subcategories/${subcategoryId}/form/publish`, {}),

  customForms: () => api.get<CustomFormRow[]>('/api/v1/admin/subcategories/custom-forms'),

  clearCustomForms: (subcategoryIds: string[]) =>
    api.post<{ results: BindResult[] }>('/api/v1/admin/subcategories/custom-forms/clear', {
      subcategoryIds,
    }),
};

export const formBindingApi = {
  catalogs: () => api.get<CatalogOption[]>('/api/v1/admin/form-binding/catalogs'),
  tree: (catalogId: string | null) =>
    api.get<BindingTreeCategory[]>(
      catalogId
        ? `/api/v1/admin/form-binding/tree?catalogId=${encodeURIComponent(catalogId)}`
        : '/api/v1/admin/form-binding/tree',
    ),
};

export const formTemplatesApi = {
  list: () => api.get<FormTemplate[]>('/api/v1/admin/form-templates'),
  get: (id: string) => api.get<FormTemplate>(`/api/v1/admin/form-templates/${id}`),
  create: (input: { name: string; description?: string | null; group?: string | null; schema: FormSchema }) =>
    api.post<FormTemplate>('/api/v1/admin/form-templates', input),
  update: (
    id: string,
    patch: { name?: string; description?: string | null; group?: string | null; schema?: FormSchema },
  ) => api.put<FormTemplate>(`/api/v1/admin/form-templates/${id}`, patch),
  remove: (id: string, bindings: 'snapshot' | 'clear' = 'snapshot') =>
    api.delete<void>(`/api/v1/admin/form-templates/${id}?bindings=${bindings}`),

  bindings: (id: string) =>
    api.get<TemplateBinding[]>(`/api/v1/admin/form-templates/${id}/bindings`),
  versions: (id: string) =>
    api.get<FormVersionInfo[]>(`/api/v1/admin/form-templates/${id}/versions`),
  preflight: (id: string, subcategoryIds: string[]) =>
    api.post<{ rows: PreflightRow[] }>(`/api/v1/admin/form-templates/${id}/preflight`, {
      subcategoryIds,
    }),
  bind: (id: string, subcategoryIds: string[]) =>
    api.post<{ results: BindResult[] }>(`/api/v1/admin/form-templates/${id}/bind`, {
      subcategoryIds,
    }),
  unbind: (id: string, subcategoryIds: string[]) =>
    api.post<{ results: BindResult[] }>(`/api/v1/admin/form-templates/${id}/unbind`, {
      subcategoryIds,
    }),
  mergePreview: (currentSchema: FormSchema, incomingSchema: FormSchema, mode: ApplyMode) =>
    api.post<FormSchema>('/api/v1/admin/form-templates/merge-preview', {
      currentSchema,
      incomingSchema,
      mode,
    }),
};

